import { inject, Injector, NgModule, runInInjectionContext } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { E404Component } from '@modules/errors/e404/e404.component';
import { AuthGuard } from '@core/guards/auth.guard';
import { PaymentMethodGuard } from '@core/guards/payment-method.guard';
import { LoginComponent } from '@modules/login/login.component';
import { NewAppComponent } from '@modules/new-app/new-app.component';
import { MyDevicesComponent } from '@modules/my-devices/my-devices.component';
import { PpdTestComponent } from '@modules/tests/ppd-test/ppd-test.component';
import { SingleAthleteMetabolicProfilingComponent } from '@modules/tests/single-athlete-metabolic-profiling/single-athlete-metabolic-profiling.component';
import { ForgotPasswordComponent } from '@modules/login/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from '@modules/login/reset-password/reset-password.component';
import { ForceUpdateDeviceComponent } from '@modules/login/force-update-device/force-update-device.component';
import { LoadAnalysisComponent } from '@modules/load-analysis/load-analysis/load-analysis.component';
import { LoginRegistrationComponent } from '@modules/login/login-registration/login-registration.component';
import { AdditionalInfoComponent } from '@modules/login/additional-info/additional-info.component';
import { ReportPreviewComponent } from '@modules/report-preview/report-preview/report-preview.component';
import { CountryResolveService } from '@shared/resolvers/country-resolve.service';
import { PrivacyPolicyComponent } from '@shared/components/privacy-policy/privacy-policy.component';
import { TermsComponent } from '@shared/components/terms/terms.component';
import { ForceUpdateConsentComponent } from '@modules/login/force-update-consent/force-update-consent.component';
import { ConfirmSharedDataComponent } from '@shared/components/confirm-shared-data/confirm-shared-data.component';
import { CourseCompleteComponent } from '@shared/components/course-complete/course-complete.component';
import { HumangoMetabolicProfileComponent } from '@modules/humango/metabolic-profile/humango-metabolic-profile.component';
import { humangoMetabolicProfileGuard } from '@core/guards/humango-metabolic-profile.guard';
import { PaymentLinkPlanComponent } from '@modules/payment-link/plan/plan.component';
import { additionalInfoGuard } from '@core/guards/additional-info.guard';
import { MyInscydComponent } from '@shared/components/my-inscyd/my-inscyd.component';
import { MyInscydFeatureItemResolver } from '@shared/resolvers/my-inscyd-feature-item.resolver';
import { MyProfileComponent } from '@shared/components/my-profile/my-profile.component';
import { MyProfileItemResolver } from '@shared/resolvers/my-profile-item.resolver';
import { IntegrationsComponent } from '@shared/components/integrations/integrations.component';
import { IntegrationListResolver } from '@shared/resolvers/integration-list.resolver';
import { AccountInfoComponent } from '@shared/components/account-info/account-info.component';
import { AccountInfoResolver } from '@shared/resolvers/account-info.resolver';
import { PersonalInfoComponent } from '@shared/components/personal-info/personal-info.component';
import { PersonalInfoResolver } from '@shared/resolvers/personal-info.resolver';
import { CurrentUserSessionResolver } from '@shared/resolvers/current-user-session.resolver';
import { CurrentUserSessionComponent } from '@shared/components/current-user-session/current-user-session.component';
import { OrgTabDescriptionTextComponent } from '@modules/administration/organization-common-setting/org-tab-description-text/org-tab-description-text.component';
import { CustomAthleteCategoryComponent } from '@shared/components/custom-athlete-category/custom-athlete-category.component';
import { SportConfigurationComponent } from '@shared/components/sport-configuration/sport-configuration.component';
import { VisualizationComponent } from '@shared/components/visualization/visualization.component';
import { SportMetricVisualizationResolver } from '@shared/resolvers/sport-metric-visualization.resolver';
import { TestEquipmentPageComponent } from '@shared/components/test-equipment-page/test-equipment-page.component';
import { MyTestEquipmentResolver } from '@shared/resolvers/my-test-equipment.resolver';
import { TestCreationComponent } from '@shared/components/test-creation/test-creation.component';
import { MetricSettingsComponent } from '@shared/components/metric-settings/metric-settings.component';
import { ReportGeneratorComponent } from '@modules/report-generator/report-generator.component';
import { FeatureFlagEnum } from '@shared/enums/feature-flag.enum';
import { featureFlagGuardFactory } from '@core/guards/feature-flag.guard';
import { MetricSettingsResolver } from '@shared/resolvers/metric-settings.resolver';
import { CourseLogoutComponent } from '@modules/courses/course-logout/course-logout.component';
import { UseCasesComponent } from '@shared/components/use-cases/use-cases.component';
import { UseCasesResolver } from '@shared/resolvers/use-cases.resolver';
import { OnboardingGuard } from '@shared/guards/onboarding.guard';
import { HomeComponent } from '@modules/home/home.component';
import { FeatureFlagsService } from '@shared/services/feature-flags/feature-flags.service';

const routes: Routes = [
  {
    path: '',
    component: LoginComponent,
  },
  {
    path: 'privacy-policy',
    component: PrivacyPolicyComponent,
  },
  {
    path: 'course-complete',
    component: CourseCompleteComponent,
  },
  {
    path: 'course-logout',
    component: CourseLogoutComponent,
  },
  {
    path: 'terms',
    component: TermsComponent,
  },
  {
    path: 'register',
    component: LoginRegistrationComponent,
  },
  {
    path: 'additional-info',
    component: AdditionalInfoComponent,
    resolve: {
      countries: CountryResolveService,
    },
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordComponent,
  },
  {
    path: 'reset-password/:key',
    component: ResetPasswordComponent,
  },
  {
    path: 'force-update-device',
    component: ForceUpdateDeviceComponent,
  },
  {
    path: 'force-update-consent',
    component: ForceUpdateConsentComponent,
  },
  {
    path: 'confirm-share-data',
    component: ConfirmSharedDataComponent,
  },
  {
    path: 'report-preview/:testId',
    component: ReportPreviewComponent,
  },
  {
    path: 'external/humango',
    component: HumangoMetabolicProfileComponent,
    canActivate: [humangoMetabolicProfileGuard],
  },
  {
    path: 'payment_link/:guid',
    component: PaymentLinkPlanComponent,
  },
  {
    path: 'report-generator',
    component: ReportGeneratorComponent,
    canActivate: [AuthGuard, featureFlagGuardFactory(FeatureFlagEnum.REPORTS_v2)],
  },
  {
    path: 'signup/:guid',
    loadComponent: () => import('@modules/signup/signup.component').then((m) => m.SignupComponent),
  },
  {
    path: 'course-purchase',
    loadChildren: () => import('./features/course-purchase/course-purchase.routes').then((m) => m.coursePurchaseRoutes),
  },
  {
    path: 'home',
    component: NewAppComponent,
    canActivate: [AuthGuard, additionalInfoGuard],
    children: [
      {
        path: 'integrations',
        component: IntegrationsComponent,
        canActivate: [AuthGuard],
        resolve: {
          data: IntegrationListResolver,
        },
      },
      {
        path: 'security',
        component: CurrentUserSessionComponent,
        canActivate: [AuthGuard],
        resolve: {
          data: CurrentUserSessionResolver,
        },
      },
      {
        path: 'custom-pdf-text',
        component: OrgTabDescriptionTextComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'custom-athlete-category',
        component: CustomAthleteCategoryComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'account-info',
        component: AccountInfoComponent,
        canActivate: [AuthGuard],
        resolve: {
          data: AccountInfoResolver,
        },
      },
      {
        path: 'personal-info',
        component: PersonalInfoComponent,
        canActivate: [AuthGuard],
        resolve: {
          data: PersonalInfoResolver,
        },
      },
      {
        path: 'sport-configuration',
        component: SportConfigurationComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'visualization',
        component: VisualizationComponent,
        canActivate: [AuthGuard],
        resolve: {
          data: SportMetricVisualizationResolver,
        },
      },

      {
        path: 'dashboard',
        canActivate: [AuthGuard, PaymentMethodGuard],
        loadChildren: () => import('./features/activation/activation.routes').then((m) => m.activationRoutes),
      },
      {
        path: 'onboarding',
        canActivate: [AuthGuard],
        loadChildren: () => import('./features/onboarding/onboarding.routes').then((m) => m.onboardingRoutes),
      },
      {
        path: 'my-devices',
        component: MyDevicesComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'metabolic-profile/:id/:action',
        component: SingleAthleteMetabolicProfilingComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'ppd/:id/:action',
        component: PpdTestComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'apc',
        loadChildren: () => import('@modules/apc/apc.module').then((m) => m.ApcModule),
      },
      {
        path: 'administration',
        loadChildren: () => import('@modules/administration/administration.module').then((m) => m.AdministrationModule),
      },
      {
        path: 'manage',
        loadChildren: () => import('@modules/manage/manage.module').then((m) => m.ManageModule),
      },
      {
        path: 'plan-subscription',
        loadChildren: () =>
          import('@modules/plan-subscription/plan-subscription.module').then((m) => m.PlanSubscriptionModule),
      },
      {
        path: 'tac',
        loadChildren: () =>
          import('@modules/teams-and-competitions/teams-and-competitions.module').then(
            (m) => m.TeamsAndCompetitionsModule
          ),
      },
      {
        path: 'dynamic-simulation',
        loadChildren: () =>
          import('@modules/dynamic-simulation/dynamic-simulation.module').then((m) => m.DynamicSimulationModule),
      },
      {
        path: 'load-analysis',
        component: LoadAnalysisComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'college',
        loadChildren: () => import('@modules/courses/college.module').then((m) => m.CollegeModule),
        canActivate: [AuthGuard, PaymentMethodGuard],
      },
      {
        path: 'feature-store',
        loadChildren: () => import('@modules/feature-store/feature-store.module').then((m) => m.FeatureStoreModule),
        canActivate: [AuthGuard, PaymentMethodGuard],
      },
      {
        path: 'my-inscyd',
        component: MyInscydComponent,
        canActivate: [AuthGuard, PaymentMethodGuard],
        resolve: {
          data: MyInscydFeatureItemResolver,
        },
      },
      {
        path: 'my-profile',
        component: MyProfileComponent,
        canActivate: [AuthGuard, PaymentMethodGuard],
        resolve: {
          data: MyProfileItemResolver,
        },
      },
      {
        path: 'test-equipment',
        component: TestEquipmentPageComponent,
        resolve: {
          data: MyTestEquipmentResolver,
        },
      },
      {
        path: 'metric-settings',
        component: MetricSettingsComponent,
        canActivate: [AuthGuard],
        resolve: {
          data: MetricSettingsResolver,
        },
      },
      {
        path: 'manual-test',
        component: TestCreationComponent,
      },
      {
        path: 'ppd-test',
        component: TestCreationComponent,
      },
      {
        path: 'virtual-test',
        component: TestCreationComponent,
      },
      {
        path: 'lactate-test',
        component: TestCreationComponent,
      },
      {
        path: '**',
        component: E404Component,
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
